<template>
  <div class="p-4 md:p-6">
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-semibold">Редактирование продукции</h1>
        <p class="text-sm text-gray-500">ID: {{ id }}</p>
      </div>
      <div class="flex gap-2">
        <button @click="goBack" class="px-4 py-2 rounded-lg border hover:bg-gray-50">Назад</button>
        <button @click="save" :disabled="saving" class="px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-60">
          {{ saving ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="flex justify-center items-center h-40">
      <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
    </div>

    <div v-else class="grid grid-cols-1 lg:grid-cols-[30%_1fr] gap-6">
      <!-- Основные поля -->
      <div class="bg-white rounded-xl shadow p-4 space-y-4">
        <div>
          <label class="block text-sm text-gray-600 mb-1">Название</label>
          <input v-model="form.name" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Введите название" />
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Артикул (SKU)</label>
          <input v-model="form.sku" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Введите артикул" />
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Единица измерения</label>
          <input
            v-if="form.guid"
            v-model="form.unit_label"
            type="text"
            disabled
            class="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-700 cursor-not-allowed"
          />
          <div v-else class="input-wrapper">
            <v-select
              v-model="form.unit_id"
              :reduce="u => u.id"
              :options="productUnits"
              label="short_name"
              placeholder="Выберите единицу"
              class="v-select-clean"
              :clearable="true"
            >
              <template #option="{ short_name, name }">
                {{ short_name }} ({{ name }})
              </template>
              <template #no-options>
                Единицы не найдены
              </template>
            </v-select>
          </div>
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Описание</label>
          <textarea v-model="form.description" rows="4" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Краткое описание" />
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Комментарий</label>
          <textarea v-model="form.comment" rows="3" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Комментарий" />
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Производитель</label>
          <div class="input-wrapper">
            <v-select
              v-model="form.manufacturer_id"
              :reduce="company => company"
              :options="companies"
              :filterable="false"
              label="name"
              placeholder="Начните вводить название или ИНН..."
              :loading="loadingCompanies"
              @search="onCompanySearch"
              class="v-select-clean"
            >
              <template #no-options="{ search, searching, loading }">
                <span v-if="loadingCompanies">Загружается...</span>
                <span v-else>Компании не найдены</span>
              </template>
            </v-select>
          </div>
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Технические условия (ТУ)</label>
          <input v-model="form.tech_conditions" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="ТУ" />
        </div>
        <div>
          <label class="block text-sm text-gray-600 mb-1">Группа продукции</label>
          <div class="input-wrapper">
            <v-select
              v-model="form.product_group_id"
              :reduce="group => group.id"
              :options="productGroupOptions"
              label="name"
              placeholder="Выберите группу"
              class="v-select-clean"
              :clearable="false"
            >
              <template #no-options="{ search, searching, loading }">
                Группы не найдены.
              </template>
            </v-select>
          </div>
        </div>

        <!-- Остатки на складах -->
        <div>
          <label class="block text-sm text-gray-600 mb-1">Остатки на складах</label>
          <div v-if="storehouseRemainsLoading" class="text-center py-3">
            <div class="inline-block animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
          </div>
          <div v-else-if="storehouseRemains.length === 0" class="text-sm text-gray-500 py-1">
            Остатков нет
          </div>
          <div v-else class="space-y-1.5">
            <div
              v-for="sw in storehouseRemains"
              :key="sw.module_id"
              class="flex justify-between items-center py-1.5 px-2 text-sm bg-gray-50 rounded border border-gray-100"
            >
              <span class="text-gray-700">{{ sw.storehouse_name }}</span>
              <span class="font-medium text-blue-600">{{ formatQuantity(sw.count) }} {{ (form.unit_label && form.unit_label !== 'Не указано') ? form.unit_label : 'шт' }}</span>
            </div>
            <div v-if="storehouseRemainsTotal > 0" class="flex justify-between items-center py-1.5 px-2 text-sm bg-gray-50 rounded border border-gray-100 mt-1">
              <span class="font-medium text-gray-800">Всего</span>
              <span class="font-medium text-blue-600">{{ formatQuantity(storehouseRemainsTotal) }} {{ (form.unit_label && form.unit_label !== 'Не указано') ? form.unit_label : 'шт' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Атрибуты и Компоненты -->
      <div class="space-y-6">
        <!-- Атрибуты -->
        <div class="bg-white rounded-xl shadow p-4 overflow-visible">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-lg font-medium">Атрибуты</h2>
              <p class="text-xs text-gray-500">Выберите атрибут и укажите значение</p>
            </div>
            <div class="flex gap-2">
              <input v-model="attributesSearch" placeholder="Поиск атрибутов..." class="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
              <button @click="fillByTemplate" class="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700">Заполнить по шаблону</button>
              <button @click="addAttributeRow" class="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Добавить атрибут</button>
            </div>
          </div>

          <div v-if="attributeRows.length === 0" class="text-sm text-gray-500">Атрибуты не добавлены</div>

          <div ref="attributesScrollContainer" class="space-y-3 max-h-[40vh] h-full overflow-auto p-1" @scroll="closeAllDropdowns">
            <div v-for="(row, index) in filteredAttributeRows" :key="index" class="grid grid-cols-12 gap-3 items-center">
              <div class="col-span-12 md:col-span-5">
                <div class="input-wrapper">
                  <v-select
                    v-model="row.product_attribute_id"
                    :reduce="attr => attr.id"
                    :options="getAvailableAttributesForRow(index)"
                    label="name"
                    placeholder="Выберите атрибут"
                    class="v-select-clean"
                    :append-to-body="true"
                  >
                    <template #no-options="{ search, searching, loading }">
                      Атрибуты не найдены.
                    </template>
                  </v-select>
                </div>
              </div>
              <div class="col-span-12 md:col-span-6">
                <input v-model="row.value" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Значение" />
              </div>
              <div class="col-span-12 md:col-span-1 flex justify-center items-center">
                <button @click="removeAttributeRow(index)" class="text-red-600 hover:text-red-800 p-1" title="Удалить"><svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
              </div>
            </div>
          </div>
        </div>

        <!-- Компоненты продукта -->
        <div class="bg-white rounded-xl shadow p-4">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-lg font-medium">Компоненты продукта</h2>
              <p class="text-xs text-gray-500">Используемые продукты в составе</p>
            </div>
            <button @click="addComponentProduct" class="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">
              <span class="mr-1">+</span> Добавить компонент
            </button>
          </div>

          <div v-if="componentRows.length === 0" class="text-sm text-gray-500">Компоненты не добавлены</div>

          <div v-if="componentRows.length > 0" class="max-h-[40vh] overflow-auto">
            <table class="w-full border-collapse border border-gray-300">
              <tbody>
                <tr v-for="(component, index) in componentRows"
                    :key="`${component.component_product_id}-${index}-${Date.now()}`"
                    class="hover:bg-gray-50 border-b border-gray-300">
                  <td class="p-1 w-8 text-center">
                    <span class="font-medium">{{ index + 1 }}.</span>
                  </td>
                  <td class="p-1">
                    <span class="font-medium">{{ getComponentDisplayName(component) }}</span>
                  </td>
                  <td class="p-1 w-20 text-center">
                    <span class="text-gray-600">x{{ component.count }}</span>
                  </td>
                  <td class="p-1 w-12 text-center">
                    <button
                      @click="confirmComponentRemoval(index)"
                      class="text-red-600 hover:text-red-800 p-1"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
                </div>

      <!-- Атрибуты ОТК -->
      <div class="bg-white rounded-xl shadow p-4 overflow-visible">
        <div class="flex items-center justify-between mb-4">
          <div>
            <h2 class="text-lg font-medium">Атрибуты ОТК</h2>
            <p class="text-xs text-gray-500">Параметры контроля качества</p>
          </div>
          <div class="flex gap-2">
            <input v-model="otkSearch" placeholder="Поиск атрибутов ОТК..." class="border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500" />
            <button @click="addOtkRow" type="button" class="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700">Добавить атрибут ОТК</button>
          </div>
        </div>

        <div v-if="otkRows.length === 0" class="text-sm text-gray-500">Атрибуты ОТК не добавлены</div>

        <div class="space-y-3 max-h-[40vh] h-full overflow-auto p-1">
          <div v-for="(row, index) in filteredOtkRows" :key="index" class="border border-gray-200 rounded-lg p-3 flex gap-3 items-start">
            <div class="flex-1 space-y-2">
              <!-- Первая строка: Параметр + Инструмент -->
              <div class="grid grid-cols-2 gap-3">
                <input v-model="row.parameter" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Параметр" />
                <input v-model="row.instrument" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Инструмент" />
              </div>
              <!-- Вторая строка: Допуск + Ед. изм. -->
              <div class="grid grid-cols-2 gap-3">
                <input v-model="row.tolerance" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Допуск" />
                <input v-model="row.unit" type="text" class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500" placeholder="Ед. изм." />
              </div>
            </div>
            <button @click="otkRows.splice(index, 1)" type="button" class="text-red-600 hover:text-red-800 p-1 mt-1" title="Удалить"><svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg></button>
          </div>
        </div>

        <!-- Блок выборки -->
        <div class="mt-4 pt-4 border-t border-gray-200">
          <p class="text-sm font-medium text-gray-700 mb-2">Выборка</p>
          <div class="flex gap-4 items-center">
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600 whitespace-nowrap">Процент выборки</label>
              <input
                v-model.number="form.otk_sample_percent"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="%"
                class="w-20 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
              <span class="text-sm text-gray-500">%</span>
            </div>
            <div class="flex items-center gap-2">
              <label class="text-sm text-gray-600 whitespace-nowrap">Но не менее</label>
              <input
                v-model.number="form.otk_min_count"
                type="number"
                min="0"
                placeholder="шт"
                class="w-20 border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              />
              <span class="text-sm text-gray-500">{{ form.unit_label || 'шт' }}</span>
            </div>
          </div>
          <p v-if="form.otk_sample_percent || form.otk_min_count" class="mt-2 text-sm text-gray-500">
            Выборка {{ form.otk_sample_percent || 0 }}%, но не менее {{ form.otk_min_count || 0 }} {{ form.unit_label || 'шт' }}
          </p>
        </div>
      </div>
      </div>
    </div>

    <!-- Модалка для добавления компонентов -->
    <div v-if="isComponentModalOpen" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div class="bg-white rounded-lg shadow-lg w-[90%] max-w-2xl p-6">
        <h3 class="text-lg font-semibold mb-4">{{ editingComponentIndex === null ? 'Добавить компонент' : 'Редактировать компонент' }}</h3>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Выберите продукт</label>
          <div class="input-wrapper">
            <v-select
              v-model="selectedComponentProduct"
              :reduce="product => product"
              :options="availableComponentProducts"
              :filterable="false"
              label="name"
              placeholder="Начните вводить название или артикул..."
              :loading="loadingComponentProducts"
              @search="onComponentProductSearch"
              class="v-select-clean"
            >
              <template #option="option">
                <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
              </template>
              <template #selected-option="option">
                <span>{{ option.sku ? option.sku + ' - ' + option.name : option.name }}</span>
              </template>
              <template #no-options="{ search }">
                <span v-if="loadingComponentProducts">Загружается...</span>
                <span v-else>Продукты не найдены</span>
              </template>
            </v-select>
          </div>
        </div>

        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-1">Количество</label>
          <input
            v-model="componentCount"
            type="number"
            min="1"
            class="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            placeholder="Введите количество"
          />
        </div>

        <p v-if="componentError" class="text-red-500 text-sm mt-1 mb-4">{{ componentError }}</p>

        <div class="flex justify-end space-x-2">
          <button
            type="button"
            class="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            @click="saveComponent"
          >
            Сохранить
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
            @click="closeComponentModal"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToleranceInput from '@/components/products/ToleranceInput.vue';

export default {
  name: 'ProductEditPage',
  components: { ToleranceInput },
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      loading: true,
      saving: false,
      form: {
        id: null,
        guid: null,
        name: '',
        sku: '',
        unit_guid: null,
        unit_label: 'шт',
        unit_id: null,
        description: '',
        comment: '',
        manufacturer_id: null,
        tech_conditions: '',
        product_group_id: null,
        otk_sample_percent: null,
        otk_min_count: null,
      },
      productUnits: [],
      allAttributes: [],
      companies: [],
      productGroups: [],
      attributeRows: [],
      attributesSearch: '',
      // Атрибуты ОТК
      otkRows: [],
      otkSearch: '',
      // Компоненты продукта
      componentRows: [],
      allProducts: [],
      isComponentModalOpen: false,
      selectedComponentProduct: null,
      componentCount: 1,
      editingComponentIndex: null,
      componentError: '',
      loadingComponentProducts: false,
      componentProductSearchTimer: null,
      loadingCompanies: false,
      companySearchTimer: null,
      storehouseRemains: [],
      storehouseRemainsLoading: false,
    };
  },
  computed: {
    filteredOtkRows() {
      if (!this.otkSearch) return this.otkRows;
      const q = this.otkSearch.toLowerCase();
      return this.otkRows.filter(r =>
        (r.parameter || '').toLowerCase().includes(q) ||
        (r.tolerance || '').toLowerCase().includes(q) ||
        (r.instrument || '').toLowerCase().includes(q)
      );
    },
    storehouseRemainsTotal() {
      return this.storehouseRemains.reduce((sum, sw) => sum + (parseFloat(sw.count) || 0), 0);
    },
    filteredAttributeRows() {
      if (!this.attributesSearch.trim()) return this.attributeRows;
      const q = this.attributesSearch.toLowerCase();
      const idSet = new Set(
        this.allAttributes
          .filter(a => `${a.name} ${a.key}`.toLowerCase().includes(q))
          .map(a => a.id)
      );
      return this.attributeRows.filter(r => idSet.has(r.product_attribute_id));
    },
    availableComponentProducts() {
      const usedProductIds = new Set(this.componentRows.map(c => c.component_product_id));
      return this.allProducts.filter(p => !usedProductIds.has(p.id));
    },
    productGroupOptions() {
      return [
        { id: null, name: 'Без группы' },
        ...this.productGroups
      ];
    },
  },
  async created() {
    this.onCompanySearch('');
    await Promise.all([
      this.fetchAllAttributes(),
      this.fetchProductGroups(),
      this.fetchProductUnits(),
      this.fetchProduct(this.id)
    ]);
    this.loading = false;
    this.fetchStorehouseRemains();
  },
  methods: {
    formatQuantity(val) {
      if (val == null || val === undefined) return '0';
      const num = Number(val);
      return isNaN(num) ? '0' : (Math.round(num * 1000) / 1000).toString();
    },
    async fetchStorehouseRemains() {
      this.storehouseRemainsLoading = true;
      try {
        const { data } = await axios.get(`/api/product/storehouse-remains/${this.id}`);
        if (data.success && data.data?.storehouses) {
          this.storehouseRemains = data.data.storehouses;
        } else {
          this.storehouseRemains = [];
        }
      } catch (e) {
        // API может быть недоступен если модуль склада не подключен
        this.storehouseRemains = [];
      } finally {
        this.storehouseRemainsLoading = false;
      }
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.back();
      } else {
        this.$router.push({ name: 'Products', query: this.$route.query || {} });
      }
    },
    async fetchAllAttributes() {
      try {
        const { data } = await axios.post('/api/product/getAttributes');
        this.allAttributes = data.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    onCompanySearch(search) {
      clearTimeout(this.companySearchTimer);
      this.companySearchTimer = setTimeout(async () => {
        this.loadingCompanies = true;
        try {
          const { data } = await axios.post('/api/company/getCompanies', {
            page: 1,
            per_page: 50,
            search: search?.trim() || ''
          });
          const d = data.data;
          this.companies = d.data || [];
        } catch (e) {
          console.error(e);
          this.companies = [];
        } finally {
          this.loadingCompanies = false;
        }
      }, 350);
    },
    async fetchProductGroups() {
      try {
        const { data } = await axios.post('/api/productGroup/getProductGroups');
        this.productGroups = data.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    async fetchProductUnits() {
      try {
        const { data } = await axios.post('/api/product-unit/getAll');
        this.productUnits = data.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    onComponentProductSearch(search) {
      clearTimeout(this.componentProductSearchTimer);
      this.componentProductSearchTimer = setTimeout(async () => {
        this.loadingComponentProducts = true;
        try {
          const { data } = await axios.post('/api/product/getProducts', {
            page: 1,
            per_page: 50,
            search: search?.trim() || ''
          });
          const d = data?.data;
          this.allProducts = d?.data || [];
        } catch (e) {
          console.error(e);
          this.allProducts = [];
        } finally {
          this.loadingComponentProducts = false;
        }
      }, 350);
    },
    async fetchProduct(id) {
      try {
        const { data } = await axios.post('/api/product/getProduct', { id });
        const p = data.product || {};
        this.form.id = p.id;
        this.form.guid = p.guid || null;
        this.form.name = p.name || '';
        this.form.sku = p.sku || '';
        this.form.unit_guid = p.unit_guid || null;
        this.form.unit_label = p.unit?.short_name || 'Не указано';
        this.form.unit_id = p.unit_id || null;
        this.form.description = p.description || '';
        this.form.comment = p.comment || '';
        this.form.manufacturer_id = p.manufacturer || null;
        this.form.tech_conditions = p.tech_conditions || '';
        this.form.product_group_id = p.product_group_id || null;
        
        // Атрибуты
        const values = (p.attribute_values || p.attributeValues || []);
        this.attributeRows = values.map(v => ({ product_attribute_id: v.product_attribute_id, value: v.value }));
        
        // Компоненты
        const components = (p.components || []);
        this.componentRows = components.map(c => ({
          component_product_id: c.component_product_id,
          count: c.count,
          componentProduct: c.component_product
        }));

        // Атрибуты ОТК
        this.form.otk_sample_percent = p.otk_sample_percent || null;
        this.form.otk_min_count = p.otk_min_count || null;
        const otkAttrs = (p.otk_attributes || []);
        this.otkRows = otkAttrs.map(a => ({
          id: a.id,
          parameter: a.parameter || '',
          tolerance: a.tolerance || '',
          unit: a.unit || '',
          instrument: a.instrument || '',
        }));
      } catch (e) {
        console.error(e);
      }
    },
    addOtkRow() {
      this.otkRows.push({ id: null, parameter: '', tolerance: '', unit: '', instrument: '' });
    },
    addAttributeRow() {
      this.attributeRows.push({ product_attribute_id: null, value: '' });
    },
    removeAttributeRow(index) {
      this.attributeRows.splice(index, 1);
    },
    getAvailableAttributesForRow(currentIndex) {
      // Исключаем атрибуты, которые уже добавлены в ДРУГИХ строках
      const usedAttributeIds = new Set(
        this.attributeRows
          .map((row, index) => index !== currentIndex ? row.product_attribute_id : null)
          .filter(id => id !== null)
      );
      return this.allAttributes.filter(attr => !usedAttributeIds.has(attr.id));
    },
    fillByTemplate() {
      // Шаблон атрибутов с заполнителями
      const templateAttributes = [
        { key: 'short_name', value: this.form.name },
        { key: 'variant', value: 'XXX' },
        { key: 'size', value: 'X мм x X мм x X мм' },
        { key: 'weight_net', value: 'X кг' },
        { key: 'weight_brut', value: 'X кг' },
        { key: 'temperature_min', value: '-X °C' },
        { key: 'temperature_max', value: 'X °C' },
        { key: 'humidity_min', value: 'X%' },
        { key: 'humidity_max', value: 'X%' },
        { key: 'pressure_min', value: 'X.X кПа' },
        { key: 'pressure_max', value: 'X.X кПа' },
        { key: 'serial_prefix', value: 'XXX' },
      ];
      
      let addedCount = 0;
      
      // Проходим по каждому шаблонному атрибуту
      templateAttributes.forEach(template => {
        // Ищем атрибут в справочнике
        const attribute = this.allAttributes.find(attr => attr.key === template.key);
        if (!attribute) return;
        
        // Проверяем, есть ли уже такой атрибут у продукта
        const existingRow = this.attributeRows.find(row => row.product_attribute_id === attribute.id);
        if (!existingRow) {
          // Добавляем новый атрибут только если его еще нет
          this.attributeRows.push({
            product_attribute_id: attribute.id,
            value: template.value
          });
          addedCount++;
        }
      });
      
      // Проверяем результат заполнения
      if (addedCount === 0) {
        alert('Все атрибуты по шаблону уже заполнены');
      }
    },
    // Методы для работы с компонентами
    addComponentProduct() {
      this.selectedComponentProduct = null;
      this.componentCount = 1;
      this.editingComponentIndex = null;
      this.componentError = '';
      this.isComponentModalOpen = true;
      this.onComponentProductSearch('');
    },
    confirmComponentRemoval(index) {
      this.componentRows.splice(index, 1);
    },
    getComponentDisplayName(component) {
      const product = component.componentProduct || this.allProducts.find(p => p.id === component.component_product_id);
      if (!product) return 'Неизвестный продукт';
      
      const hasSku = product.sku && product.sku.trim() !== '';
      const hasName = product.name && product.name.trim() !== '';
      
      if (hasSku && hasName) {
        return `${product.sku} - ${product.name}`;
      } else if (hasSku) {
        return product.sku;
      } else if (hasName) {
        return product.name;
      } else {
        return 'Без названия';
      }
    },
    saveComponent() {
      this.componentError = '';
      
      if (!this.selectedComponentProduct) {
        this.componentError = 'Выберите продукт';
        return;
      }
      
      if (!this.componentCount || this.componentCount < 1) {
        this.componentError = 'Укажите количество больше 0';
        return;
      }
      
      // Проверка на дублирование (если не редактируем)
      if (this.editingComponentIndex === null) {
        const exists = this.componentRows.some(c => c.component_product_id === this.selectedComponentProduct?.id);
        if (exists) {
          this.componentError = 'Этот продукт уже добавлен в компоненты';
          return;
        }
      }
      
      const componentData = {
        component_product_id: this.selectedComponentProduct?.id ?? null,
        count: parseInt(this.componentCount),
        componentProduct: this.selectedComponentProduct
      };
      
      if (this.editingComponentIndex !== null) {
        // Редактирование
        this.componentRows[this.editingComponentIndex] = componentData;
      } else {
        // Добавление
        this.componentRows.push(componentData);
      }
      
      this.closeComponentModal();
    },
    closeComponentModal() {
      this.isComponentModalOpen = false;
      this.selectedComponentProduct = null;
      this.componentCount = 1;
      this.editingComponentIndex = null;
      this.componentError = '';
    },
    async save() {
      this.saving = true;
      try {
        const payload = {
          id: this.form.id,
          name: this.form.name,
          sku: this.form.sku,
          description: this.form.description,
          comment: this.form.comment,
          manufacturer_id: this.form.manufacturer_id?.id || null,
          tech_conditions: this.form.tech_conditions,
          product_group_id: this.form.product_group_id,
          guid: this.form.guid || undefined,
          unit_guid: this.form.unit_guid || undefined,
          unit_id: this.form.unit_guid ? undefined : this.form.unit_id,
          attributes: this.attributeRows
            .filter(r => r.product_attribute_id && r.value !== undefined)
            .map(r => ({ product_attribute_id: r.product_attribute_id, value: r.value })),
          components: this.componentRows
            .filter(r => r.component_product_id && r.count > 0)
            .map(r => ({ component_product_id: r.component_product_id, count: r.count })),
          otk_sample_percent: this.form.otk_sample_percent,
          otk_min_count: this.form.otk_min_count,
          otk_attributes: this.otkRows
            .filter(r => r.parameter)
            .map(r => ({ id: r.id, parameter: r.parameter, tolerance: r.tolerance, unit: r.unit, instrument: r.instrument })),
        };
        await axios.post('/api/product/createOrUpdateProduct', payload);
        this.$router.push({ name: 'Products' });
      } catch (e) {
        console.error(e);
      } finally {
        this.saving = false;
      }
    },
    closeAllDropdowns() {
      // Закрываем все открытые v-select при скролле
      document.querySelectorAll('.vs__dropdown-menu').forEach(dropdown => {
        dropdown.style.display = 'none';
      });
      // Снимаем фокус со всех v-select
      document.querySelectorAll('.v-select input').forEach(input => {
        input.blur();
      });
    },
  },
};
</script>

<style scoped>
.input-wrapper {
  @apply border rounded-lg px-3 focus-within:ring-2 focus-within:ring-blue-500;
  border-color: #e5e7eb;
}

.v-select-clean :deep(.vs__dropdown-toggle) {
  border: none;
}

.v-select-clean :deep(.vs__search),
.v-select-clean :deep(.vs__selected) {
  margin: 0;
}
</style>

